import { FC } from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { TitleAndSubtitleDisplay } from '@/atoms/TitleAndSubtitleDisplay'
import { paths } from '@/constants/paths'
import { LandscapeRowCollection, LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { ContentSneakPeek } from '@/services/ProjectsService'
import { LinkViewModel } from '@/services/RenderService'
import { useSafeTrack } from '@/utils/analytics'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  testId?: string
  sneakPeekTitleCollection?: ContentSneakPeek[] | undefined
  title?: string
}

export const SneakPeekCollection: FC<Props> = ({ testId, sneakPeekTitleCollection, title }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')

  const handleTracking = () => {
    track('Sneak Peek Rail Item Clicked')
  }

  const validSneakPeeks = Array.isArray(sneakPeekTitleCollection) ? sneakPeekTitleCollection.filter(isDefined) : []

  if (validSneakPeeks.length === 0) {
    return null
  }

  return (
    <LandscapeRowCollection className="relative" testId={testId} title={title ? title : t('sneakPeeks', 'Sneak Peeks')}>
      {validSneakPeeks.map((item: ContentSneakPeek) => {
        if (!item) return null

        const linkModel = buildLinkModel(item)
        if (!linkModel) return null

        return (
          <LazyHydrate key={item?.slug} whenVisible>
            <>
              <LandscapeTileCard className="mb-2 mr-7" linkModel={linkModel} onClick={handleTracking}>
                <PlayButtonThumbnailOverlay />
              </LandscapeTileCard>
              <TitleAndSubtitleDisplay
                className="mr-4"
                title={t('sneakPeek', 'Sneak Peek')}
                subtitle={item?.project?.name}
                duration={item.duration}
              />
            </>
          </LazyHydrate>
        )
      })}
    </LandscapeRowCollection>
  )
}

const buildLinkModel = (item: ContentSneakPeek): LinkViewModel | null => {
  const href = `${paths.watch.index}/${item.project.slug}/episode/${item.id}`
  return {
    alt: item.subtitle,
    label: item.project.name ?? '',
    imageUrl: item.posterLandscapeCloudinaryPath ?? item.project.title?.image?.cloudinaryPath,
    linkUrl: href,
    track: {
      eventName: 'Content Link Clicked',
      payload: {
        linkUrl: href,
        projectSlug: item.project.slug,
        guid: item.id ?? item.project.id,
      },
    },
  }
}
